/**
 * CustomBuscaAcao — achar UMA ação entre as 74, digitando.
 *
 * Nasceu compartilhado porque duas seções precisam da mesma busca: "Quem pode o
 * quê" (que só consulta) e "Dar acesso" (que distribui). Duas cópias do mesmo
 * campo seriam duas chances de uma achar o que a outra não acha.
 *
 * PROCURA EM TUDO — módulo, recurso, ação, descrição e o código técnico —, e
 * sem acento: quem digita "execucao" acha "execução", e quem sabe o nome
 * técnico (`viagens.resumo.ver`) acha por ele.
 */
import { useState } from 'react';

export interface Modulo {
  codigo: string;
  nome: string;
  recursos: { codigo: string; nome: string; acoes: { codigo: string; nome: string; descricao?: string }[] }[];
}

export interface AcaoAchada {
  codigo: string;
  rotulo: string;
  modulo: string;
  descricao: string;
}

/** Sem acento e em minúsculas — para "depósito" ser achado digitando "deposito". */
export function crua(texto: string): string {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

export function acoesDoCatalogo(catalogo: Modulo[]): AcaoAchada[] {
  return catalogo.flatMap((m) =>
    m.recursos.flatMap((r) =>
      r.acoes.map((a) => ({
        codigo: `${m.codigo}.${r.codigo}.${a.codigo}`,
        rotulo: `${r.nome} · ${a.nome}`,
        modulo: m.nome,
        descricao: a.descricao ?? '',
      })),
    ),
  );
}

interface Props {
  catalogo: Modulo[];
  /** A ação escolhida hoje (ou `null`). Quem manda no estado é quem usa. */
  escolhida: string | null;
  aoEscolher: (permissao: string | null) => void;
  placeholder?: string;
  /** O que mostrar quando não há busca nem escolha — cada seção tem o seu. */
  vazio?: React.ReactNode;
  /** Foco ao montar: a seção que existe PARA buscar começa com o cursor aqui. */
  focoInicial?: boolean;
}

export function CustomBuscaAcao({
  catalogo,
  escolhida,
  aoEscolher,
  placeholder = 'Digite uma ação: ambiente, execução, despesa, viagem…',
  vazio,
  focoInicial = false,
}: Props) {
  const [busca, setBusca] = useState('');

  const todas = acoesDoCatalogo(catalogo);
  const termos = crua(busca).split(/\s+/).filter(Boolean);
  const achadas = termos.length
    ? todas.filter((a) => {
        const alvo = crua(`${a.modulo} ${a.rotulo} ${a.descricao} ${a.codigo}`);
        return termos.every((t) => alvo.includes(t));
      })
    : [];
  const acao = todas.find((a) => a.codigo === escolhida);

  return (
    <>
      <div className="input-group input-group-md rounded mb-2">
        <span className="input-group-text text-theme-1">
          <i className="bi bi-search"></i>
        </span>
        <input
          type="text"
          className="form-control"
          value={busca}
          placeholder={placeholder}
          aria-label="Procurar uma ação"
          autoFocus={focoInicial}
          onChange={(e) => {
            setBusca(e.target.value);
            aoEscolher(null);
          }}
        />
        {(busca || escolhida) && (
          <button
            type="button"
            className="btn btn-link"
            aria-label="Limpar a busca"
            onClick={() => {
              setBusca('');
              aoEscolher(null);
            }}
          >
            <i className="bi bi-x-lg"></i>
          </button>
        )}
      </div>

      {!busca && !escolhida && vazio}

      {busca && !escolhida && (
        <>
          <p className="text-secondary small">
            {achadas.length === 0
              ? 'Nenhuma ação com esse nome.'
              : achadas.length === 1
                ? '1 ação encontrada — é esta.'
                : `${achadas.length} ações encontradas — escolha uma.`}
          </p>
          <ul className="list-group list-group-flush bg-none">
            {achadas.slice(0, 40).map((a) => (
              <li className="list-group-item" key={a.codigo}>
                <a role="button" className="d-block" onClick={() => aoEscolher(a.codigo)}>
                  <span className="fw-medium">{a.rotulo}</span>
                  <span className="d-block small text-secondary">
                    {a.modulo}
                    {a.descricao ? ` — ${a.descricao}` : ''}
                  </span>
                </a>
              </li>
            ))}
          </ul>
          {achadas.length > 40 && (
            <p className="text-secondary small mt-2">
              Mostrando 40 de {achadas.length}. Digite mais para estreitar.
            </p>
          )}
        </>
      )}

      {acao && (
        <div className="mb-3">
          <h6 className="mb-0">{acao.rotulo}</h6>
          <p className="text-secondary small mb-0">
            {acao.modulo}
            {acao.descricao ? ` — ${acao.descricao}` : ''}
          </p>
        </div>
      )}
    </>
  );
}
